// Shared Nike tokens + formatters for the KYC Stats page.
// Redeclared here (not imported from ToolsHome) to match the house spec exactly.

export const EASE = [0.16, 1, 0.3, 1];
export const FONT = '"Helvetica Neue", Helvetica, Arial, sans-serif';

// Semantic accents — used SPARINGLY, only to encode DATA meaning (never chrome).
// Each stays its hue in both light + dark mode.
export const ACCENT = {
  green: '#1D9E75', // automation / positive
  greenDeep: '#0F6E56',
  amber: '#BA7517', // manual
  amberDeep: '#854F0B',
  red: '#E24B4A', // outliers / missing / negative
  redDeep: '#A32D2D',
  blue: '#378ADD', // info (Missing-API icon)
  blueDeep: '#185FA5',
  purple: '#534AB7', // level / neutral flourish (28× glyph, roadmap edge)
};

export const DASH = '—'; // em-dash for null fields

// A finite number? (null / undefined / NaN → false)
export const isNum = (v) => v != null && Number.isFinite(Number(v));

// Locale integer with tabular grouping. null → em-dash.
export function fmtNum(v) {
  if (!isNum(v)) return DASH;
  return Number(v).toLocaleString();
}

// Percentage with up to 1 decimal. null → em-dash. (e.g. 83.45 → "83.5%")
export function fmtPct(v, decimals = 1) {
  if (!isNum(v)) return DASH;
  const factor = Math.pow(10, decimals);
  const r = Math.round(Number(v) * factor) / factor;
  return `${r}%`;
}

// Seconds → "X min Y sec" (and "X h Y min Z sec" past an hour). null → em-dash.
// e.g. 27 → "0 min 27 sec", 384 → "6 min 24 sec", 21782 → "6 h 3 min 2 sec".
export function fmtMinSec(seconds) {
  if (!isNum(seconds)) return DASH;
  let s = Math.max(0, Math.round(Number(seconds)));
  const h = Math.floor(s / 3600);
  s -= h * 3600;
  const m = Math.floor(s / 60);
  const sec = s - m * 60;
  return h > 0 ? `${h} h ${m} min ${sec} sec` : `${m} min ${sec} sec`;
}

// Seconds → compact, day-aware duration for EXTREMES (per-stream min/max, outlier
// waits). Shows the two largest units so a genuine upstream anomaly (e.g. a case
// that sat ~118 days) reads as "118d 9h" rather than "2841 h 49 min". null → em-dash.
export function fmtDurCompact(seconds) {
  if (!isNum(seconds)) return DASH;
  let s = Math.max(0, Math.round(Number(seconds)));
  const d = Math.floor(s / 86400); s -= d * 86400;
  const h = Math.floor(s / 3600); s -= h * 3600;
  const m = Math.floor(s / 60); const sec = s - m * 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

// Seconds → human "Xm Ys" / "Xh Ym Zs" (longer-form labels). null → em-dash.
export function fmtDuration(seconds) {
  if (!isNum(seconds)) return DASH;
  let s = Math.max(0, Math.round(Number(seconds)));
  const h = Math.floor(s / 3600);
  s -= h * 3600;
  const m = Math.floor(s / 60);
  s -= m * 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

// ISO hour → short Belgrade-aware label "Jun 1, 14:00".
export function fmtHour(iso) {
  if (!iso) return DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return DASH;
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'Europe/Belgrade',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d);
  } catch {
    return iso;
  }
}

// 'YYYY-MM-DD' → "Jun 1" (display label for daily bars).
export function fmtDayShort(dayStr) {
  if (!dayStr) return DASH;
  const [y, m, d] = String(dayStr).split('-').map(Number);
  if (!y || !m || !d) return dayStr;
  const date = new Date(Date.UTC(y, m - 1, d));
  return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' }).format(date);
}

// ISO timestamp → compact "HH:mm:ss" local read time for the source badge.
export function fmtClock(iso) {
  if (!iso) return DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return DASH;
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
}

// Honor prefers-reduced-motion (snap, no wipes/shimmer loops).
export const reducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;
